import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";

/**
 * 図面 PDF の取り込みダイアログ（図面タブの「図面をアップロード」から開く）。
 *
 * 以前は一覧の上にドロップ枠を常時置いていたが、取り込むときにしか使わないのに
 * 縦を 100px ほど占めていた。ダイアログに移すことで一覧が広くなり、**ドロップで
 * 入れられる良さも残る**（枠はここにある）。
 *
 * **送ったら閉じる。** 取り込みの進み具合は一覧側の進捗バーが出す。枚数が多いと
 * 数十秒かかるので、ダイアログを開いたまま待たせると他の操作ができなくなる。
 */
export default function DrawingImportDialog({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** 選んだ PDF を渡す。取り込みと進捗の表示は呼び出し側が持つ。 */
  onSubmit: (files: File[]) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setFiles([]);
  }, [isOpen]);

  const submit = () => {
    if (files.length === 0) return;
    onSubmit(files);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>図面の取り込み</span>
          <span className="text-small font-normal text-default-500">
            {/* JSX は改行を空白に変えるので、日本語の文は 1 つの文字列で渡す
                （途中で折り返すと「取り込んだ あとに」のような隙間が出る）。 */}
            {"複数ページの PDF はページごとの図面に分けて取り込みます。" +
              "図番は取り込んだあとに図面から読み取ります（少し遅れて入ります）。"}
          </span>
        </ModalHeader>

        <ModalBody>
          <FileDropZone
            label="図面PDF"
            accept=".pdf"
            showLabel={false}
            multiple
            files={files}
            onSelectMany={setFiles}
          />
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="drawing-import-submit"
            isDisabled={files.length === 0}
            onPress={submit}
          >
            {files.length > 1 ? `${files.length} 件を取り込む` : "取り込む"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
